import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import ChatPanel from '../components/ChatPanel';
import { toast } from 'sonner';
import {
  ArrowLeft, Clock, User, Tag, DollarSign, Paperclip,
  CheckSquare, Square, Star, MessageSquare, ListChecks,
  Brain, MapPin, Sparkles, Target, ShoppingCart, Package, Loader2 as Loader2Icon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import SlaTimer from '../components/SlaTimer';

const STATUS_COLORS = {
  new: 'badge-new', assigned: 'badge-assigned', in_progress: 'badge-in_progress',
  pending_approval: 'badge-pending_approval', resolved: 'badge-resolved',
  closed: 'badge-closed', escalated: 'badge-escalated',
};
const PRIORITY_COLORS = { low: 'badge-low', medium: 'badge-medium', high: 'badge-high', critical: 'badge-critical' };
const ALL_STATUSES = ['new', 'assigned', 'in_progress', 'pending_approval', 'resolved', 'closed', 'escalated'];

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [logs, setLogs] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  // Review state
  const [showReview, setShowReview] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  // AI + Dispatch state
  const [dispatchRecs, setDispatchRecs] = useState([]);
  const [triaging, setTriaging] = useState(false);
  const [slaStatus, setSlaStatus] = useState(null);
  // AI Product Recommendations
  const [productRecs, setProductRecs] = useState(null);
  const [loadingRecs, setLoadingRecs] = useState(false);

  const fetchTicket = async () => {
    try {
      const [ticketRes, logsRes, checklistRes, slaRes] = await Promise.all([
        api.get(`/api/tickets/${id}`),
        api.get(`/api/activity/ticket/${id}`),
        api.get(`/api/tickets/${id}/checklist`),
        api.get(`/api/sla/status/${id}`),
      ]);
      setTicket(ticketRes.data);
      setLogs(logsRes.data.logs || []);
      setChecklist(checklistRes.data.checklist || null);
      setSlaStatus(slaRes.data || null);

      if (['platform_admin', 'business_admin'].includes(user?.role)) {
        const [techRes, dispatchRes] = await Promise.all([
          api.get('/api/technicians'),
          api.get(`/api/dispatch/recommend?ticket_id=${id}`).catch(() => ({ data: { recommendations: [] } })),
        ]);
        setTechnicians(techRes.data.technicians || []);
        setDispatchRecs(dispatchRes.data.recommendations || []);
      }

      // Check for existing review
      if (ticketRes.data.package_id) {
        try {
          const { data } = await api.get(`/api/reviews/${ticketRes.data.package_id}`);
          const myReview = (data.reviews || []).find(r => r.ticket_id === id && r.user_id === user?.id);
          if (myReview) setExistingReview(myReview);
        } catch (e) { /* handled */ }
      }
    } catch (err) {
      toast.error('Failed to load ticket');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTicket(); }, [id]);

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/api/tickets/${id}`, { status: newStatus });
      toast.success('Status updated');
      fetchTicket();
    } catch { toast.error('Update failed'); }
  };

  const handleAssign = async (techId) => {
    try {
      await api.put(`/api/tickets/${id}`, { assigned_to: techId });
      toast.success('Technician assigned');
      fetchTicket();
    } catch { toast.error('Assignment failed'); }
  };

  const handleRunTriage = async () => {
    setTriaging(true);
    try {
      const { data } = await api.post('/api/ai/triage', { ticket_id: id });
      if (data.analysis && !data.analysis.error) {
        setTicket(prev => ({ ...prev, ai_analysis: data.analysis }));
        toast.success('AI triage complete');
      } else {
        toast.error('AI triage failed');
      }
    } catch { toast.error('Triage failed'); }
    finally { setTriaging(false); }
  };

  const handleApplyTriage = async (fields) => {
    try {
      const { data } = await api.post(`/api/ai/triage/${id}/apply`, { apply: fields });
      setTicket(data);
      toast.success('AI suggestions applied');
    } catch { toast.error('Apply failed'); }
  };

  const handleToggleStep = async (stepId, currentCompleted) => {
    try {
      const { data } = await api.put(`/api/tickets/${id}/checklist/${stepId}`, {
        completed: !currentCompleted,
      });
      setChecklist(data.checklist);
      toast.success(!currentCompleted ? 'Step completed' : 'Step unchecked');
    } catch { toast.error('Update failed'); }
  };

  const handleSubmitReview = async () => {
    if (!ticket?.package_id) return;
    setSubmittingReview(true);
    try {
      const { data } = await api.post('/api/reviews', {
        package_id: ticket.package_id,
        ticket_id: id,
        rating: reviewRating,
        comment: reviewComment,
      });
      setExistingReview(data);
      setShowReview(false);
      toast.success('Review submitted — thank you!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse w-8 h-8 rounded-lg bg-[#D4A843]/20" />
      </div>
    );
  }
  if (!ticket) return null;

  const canChangeStatus = user?.role !== 'customer' || ['pending_approval'].includes(ticket.status);
  const canAssign = ['platform_admin', 'business_admin'].includes(user?.role);
  const isResolved = ['resolved', 'closed'].includes(ticket.status);
  const canReview = isResolved && ticket.package_id && ticket.created_by === user?.id && !existingReview;

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-8" data-testid="ticket-detail-page">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 animate-fade-in" data-testid="ticket-header">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${STATUS_COLORS[ticket.status]}`}>{ticket.status.replace('_', ' ')}</span>
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${PRIORITY_COLORS[ticket.priority]}`}>{ticket.priority}</span>
                  </div>
                  <h1 className="font-heading text-xl font-bold text-gray-900 tracking-tight" data-testid="ticket-title">{ticket.title}</h1>
                  <p className="text-xs text-gray-400 font-mono mt-1">ID: {ticket.id}</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap" data-testid="ticket-description">{ticket.description}</p>
            </div>

            {/* SLA Timer */}
            {slaStatus && slaStatus.status !== 'none' && (
              <div className="animate-fade-in stagger-1">
                <SlaTimer slaStatus={slaStatus} />
              </div>
            )}

            {/* Details Grid */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 animate-fade-in stagger-1">
              <h2 className="font-heading text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Details</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="flex items-start gap-2.5">
                  <Tag className="w-4 h-4 text-gray-400 mt-0.5" strokeWidth={1.5} />
                  <div><p className="text-xs text-gray-500">Category</p><p className="text-sm font-medium text-gray-900">{ticket.category}</p></div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Clock className="w-4 h-4 text-gray-400 mt-0.5" strokeWidth={1.5} />
                  <div><p className="text-xs text-gray-500">Service Type</p><p className="text-sm font-medium text-gray-900">{ticket.service_type}</p></div>
                </div>
                <div className="flex items-start gap-2.5">
                  <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" strokeWidth={1.5} />
                  <div><p className="text-xs text-gray-500">Pricing</p><p className="text-sm font-medium text-gray-900">{ticket.pricing_mode}{ticket.price_amount ? ` — $${ticket.price_amount}` : ''}</p></div>
                </div>
                <div className="flex items-start gap-2.5">
                  <User className="w-4 h-4 text-gray-400 mt-0.5" strokeWidth={1.5} />
                  <div><p className="text-xs text-gray-500">Created By</p><p className="text-sm font-medium text-gray-900">{ticket.creator_name}</p></div>
                </div>
                <div className="flex items-start gap-2.5">
                  <User className="w-4 h-4 text-gray-400 mt-0.5" strokeWidth={1.5} />
                  <div><p className="text-xs text-gray-500">Assigned To</p><p className="text-sm font-medium text-gray-900">{ticket.assignee_name || 'Unassigned'}</p></div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Clock className="w-4 h-4 text-gray-400 mt-0.5" strokeWidth={1.5} />
                  <div><p className="text-xs text-gray-500">Created</p><p className="text-sm font-medium text-gray-900">{new Date(ticket.created_at).toLocaleString()}</p></div>
                </div>
              </div>
              {ticket.attachments?.length > 0 && (
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Paperclip className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
                    <p className="text-xs text-gray-500 font-medium">Attachments ({ticket.attachments.length})</p>
                  </div>
                  <div className="space-y-1">
                    {ticket.attachments.map((a, i) => (
                      <span key={`item-${i}`} className="inline-block text-xs font-mono text-[#D4A843] bg-[#D4A843]/5 px-2 py-1 rounded mr-2">{a.split('/').pop()}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Workflow Checklist */}
            {checklist && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 animate-fade-in stagger-2" data-testid="workflow-checklist">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ListChecks className="w-4 h-4 text-[#D4A843]" strokeWidth={1.5} />
                    <h2 className="font-heading text-sm font-bold text-gray-900 uppercase tracking-wider">{checklist.workflow_name || 'Workflow Checklist'}</h2>
                  </div>
                  <span className="text-xs font-semibold text-[#D4A843]">{checklist.progress || 0}%</span>
                </div>
                <Progress value={checklist.progress || 0} className="h-2 mb-4" />
                <div className="space-y-1.5">
                  {(checklist.steps || []).map((step) => (
                    <button
                      key={step.id}
                      onClick={() => handleToggleStep(step.id, step.completed)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                        step.completed ? 'bg-emerald-50' : 'hover:bg-gray-50'
                      }`}
                      data-testid={`checklist-step-${step.id}`}
                    >
                      {step.completed ? (
                        <CheckSquare className="w-4 h-4 text-emerald-500 shrink-0" strokeWidth={2} />
                      ) : (
                        <Square className="w-4 h-4 text-gray-300 shrink-0" strokeWidth={1.5} />
                      )}
                      <span className={`text-sm ${step.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                        {step.title}
                      </span>
                      {step.required && !step.completed && (
                        <span className="ml-auto text-[10px] font-semibold text-red-400 uppercase tracking-wider">Required</span>
                      )}
                      {step.completed && step.completed_at && (
                        <span className="ml-auto text-[10px] text-gray-400">{new Date(step.completed_at).toLocaleString()}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* AI Triage Analysis */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 animate-fade-in stagger-2" data-testid="ai-triage-section">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-[#D4A843]" strokeWidth={1.5} />
                  <h2 className="font-heading text-sm font-bold text-gray-900 uppercase tracking-wider">AI Triage</h2>
                </div>
                {!ticket.ai_analysis && (
                  <Button size="sm" onClick={handleRunTriage} disabled={triaging} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="run-triage-btn">
                    <Sparkles className="w-3 h-3 mr-1" /> {triaging ? 'Analyzing...' : 'Run AI Triage'}
                  </Button>
                )}
              </div>

              {ticket.ai_analysis && !ticket.ai_analysis.error ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Category</p>
                      <p className="text-sm font-semibold text-gray-900">{ticket.ai_analysis.category}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Priority</p>
                      <p className={`text-sm font-semibold ${ticket.ai_analysis.priority === 'critical' ? 'text-red-600' : ticket.ai_analysis.priority === 'high' ? 'text-orange-600' : 'text-gray-900'}`}>
                        {ticket.ai_analysis.priority}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Complexity</p>
                      <p className="text-sm font-semibold text-gray-900">{ticket.ai_analysis.estimated_complexity}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 mb-1.5">AI Summary</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{ticket.ai_analysis.summary}</p>
                  </div>

                  {ticket.ai_analysis.required_skills?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1.5">Required Skills</p>
                      <div className="flex flex-wrap gap-1.5">
                        {ticket.ai_analysis.required_skills.map((skill, i) => (
                          <span key={`item-${i}`} className="text-xs font-medium bg-[#D4A843]/10 text-[#D4A843] px-2 py-0.5 rounded">{skill}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {ticket.ai_analysis.suggested_resolution_steps?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1.5">Suggested Steps</p>
                      <ol className="space-y-1.5 list-decimal list-inside">
                        {ticket.ai_analysis.suggested_resolution_steps.map((step, i) => (
                          <li key={`item-${i}`} className="text-xs text-gray-600 leading-relaxed">{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {['platform_admin', 'business_admin', 'technician'].includes(user?.role) && (
                    <div className="pt-3 border-t border-gray-100 flex gap-2">
                      <Button size="sm" variant="outline" className="text-xs rounded" onClick={() => handleApplyTriage(['category', 'priority'])} data-testid="apply-triage-btn">
                        <Target className="w-3 h-3 mr-1" /> Apply Category & Priority
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs rounded" onClick={() => handleApplyTriage(['category', 'priority', 'service_type'])} data-testid="apply-all-triage-btn">
                        Apply All Suggestions
                      </Button>
                    </div>
                  )}

                  <p className="text-[10px] text-gray-300 font-mono">GPT-5.2 | {new Date(ticket.ai_analysis.analyzed_at).toLocaleString()}</p>
                </div>
              ) : ticket.ai_analysis?.error ? (
                <p className="text-sm text-red-500">Triage failed: {ticket.ai_analysis.error}</p>
              ) : (
                <p className="text-sm text-gray-400">No AI analysis yet. Click "Run AI Triage" to analyze this ticket.</p>
              )}
            </div>

            {/* Dispatch Recommendations (admin only) */}
            {dispatchRecs.length > 0 && ['platform_admin', 'business_admin'].includes(user?.role) && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 animate-fade-in stagger-2" data-testid="dispatch-section">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-4 h-4 text-[#D4A843]" strokeWidth={1.5} />
                  <h2 className="font-heading text-sm font-bold text-gray-900 uppercase tracking-wider">Dispatch Recommendations</h2>
                </div>
                <div className="space-y-2">
                  {dispatchRecs.slice(0, 5).map((rec, i) => (
                    <div key={rec.technician_id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors" data-testid={`dispatch-rec-${i}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-gray-200 text-gray-600'}`}>
                          #{i + 1}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{rec.technician_name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${rec.availability === 'available' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              {rec.availability}
                            </span>
                            {rec.distance_km !== null && (
                              <span className="text-[10px] text-gray-400">{rec.distance_km} km away</span>
                            )}
                            <span className="text-[10px] text-gray-400">${rec.hourly_rate}/hr</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-xs font-bold text-[#D4A843]">{rec.score} pts</p>
                          <p className="text-[10px] text-gray-400">{rec.total_jobs} jobs</p>
                        </div>
                        <Button size="sm" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded text-xs px-3" onClick={() => handleAssign(rec.technician_id)} data-testid={`assign-rec-${i}`}>
                          Assign
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI Product Recommendations */}
            {['platform_admin', 'business_admin', 'technician'].includes(user?.role) && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 animate-fade-in" data-testid="product-recommendations">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-[#D4A843]" />
                    <h2 className="font-heading text-sm font-bold text-gray-900 uppercase tracking-wider">Product Suggestions</h2>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-lg text-xs" disabled={loadingRecs} onClick={async () => {
                    setLoadingRecs(true);
                    try {
                      const { data } = await api.post('/api/techfinder/ticket-recommend', { title: ticket.title, description: ticket.description, category: ticket.category });
                      setProductRecs(data);
                    } catch { toast.error('Recommendation failed'); } finally { setLoadingRecs(false); }
                  }} data-testid="get-recommendations-btn">
                    {loadingRecs ? <Loader2Icon className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} {productRecs ? 'Refresh' : 'Find Products'}
                  </Button>
                </div>
                {productRecs && (
                  <div className="space-y-2">
                    {productRecs.recommendations?.length > 0 ? productRecs.recommendations.map((rec, i) => (
                      <div key={`item-${i}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`product-rec-${i}`}>
                        <Package className="w-10 h-10 p-2 text-gray-400 bg-gray-100 rounded-lg shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{rec.name}</p>
                          <p className="text-[10px] text-gray-500">{rec.reason}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {rec.estimated_price && <span className="text-xs font-bold text-emerald-600">{rec.estimated_price}</span>}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${rec.urgency === 'critical' ? 'bg-red-100 text-red-700' : rec.urgency === 'recommended' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{rec.urgency}</span>
                          </div>
                        </div>
                        {rec.amazon_url && (
                          <a href={rec.amazon_url} target="_blank" rel="noopener noreferrer" className="shrink-0 px-3 py-1.5 bg-[#FF9900] hover:bg-[#e8890a] text-white text-[10px] font-bold rounded-lg transition-colors flex items-center gap-1">
                            <ShoppingCart className="w-3 h-3" /> Buy
                          </a>
                        )}
                      </div>
                    )) : (
                      <p className="text-xs text-gray-400 text-center py-3">{productRecs.advice || 'No product recommendations for this issue.'}</p>
                    )}
                    {productRecs.advice && productRecs.recommendations?.length > 0 && (
                      <p className="text-[10px] text-gray-500 bg-blue-50 p-2 rounded-lg mt-2">{productRecs.advice}</p>
                    )}
                    {productRecs.amazon_search_url && (
                      <a href={productRecs.amazon_search_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#D4A843] hover:underline">Browse more on Amazon</a>
                    )}
                  </div>
                )}
                {!productRecs && !loadingRecs && <p className="text-xs text-gray-400">Click "Find Products" for AI-powered product suggestions with Amazon links.</p>}
              </div>
            )}

            {/* Actions */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 animate-fade-in stagger-2" data-testid="ticket-actions">
              <h2 className="font-heading text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Actions</h2>
              <div className="flex flex-wrap gap-3">
                {canChangeStatus && (
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Change Status</label>
                    <Select value={ticket.status} onValueChange={handleStatusChange}>
                      <SelectTrigger className="w-44 rounded-lg" data-testid="status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{ALL_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {canAssign && (
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Assign Technician</label>
                    <Select value={ticket.assigned_to || ''} onValueChange={handleAssign}>
                      <SelectTrigger className="w-44 rounded-lg" data-testid="assign-select"><SelectValue placeholder="Select tech" /></SelectTrigger>
                      <SelectContent>{technicians.map(t => <SelectItem key={t.user_id} value={t.user_id}>{t.user_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Review Section */}
            {(canReview || existingReview) && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 animate-fade-in stagger-3" data-testid="review-section">
                <div className="flex items-center gap-2 mb-4">
                  <MessageSquare className="w-4 h-4 text-[#D4A843]" strokeWidth={1.5} />
                  <h2 className="font-heading text-sm font-bold text-gray-900 uppercase tracking-wider">Service Review</h2>
                </div>

                {existingReview ? (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-gray-900">Your Review</span>
                      <div className="flex">
                        {Array.from({ length: 5 }, (_, i) => (
                          <Star key={`item-${i}`} className={`w-4 h-4 ${i < existingReview.rating ? 'text-amber-400' : 'text-gray-200'}`} fill="currentColor" strokeWidth={0} />
                        ))}
                      </div>
                    </div>
                    {existingReview.comment && <p className="text-sm text-gray-600">{existingReview.comment}</p>}
                    <p className="text-[10px] text-gray-400 mt-2">Submitted {new Date(existingReview.created_at).toLocaleDateString()}</p>
                  </div>
                ) : canReview && !showReview ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500 mb-3">This ticket is resolved. How was the service?</p>
                    <Button onClick={() => setShowReview(true)} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="write-review-btn">
                      <Star className="w-4 h-4 mr-2" /> Write a Review
                    </Button>
                  </div>
                ) : showReview && (
                  <div className="space-y-4" data-testid="review-form">
                    <div>
                      <label className="text-xs text-gray-500 block mb-2">Rating</label>
                      <div className="flex gap-1">
                        {Array.from({ length: 5 }, (_, i) => (
                          <button
                            key={`item-${i}`}
                            type="button"
                            onClick={() => setReviewRating(i + 1)}
                            className="p-0.5 transition-transform hover:scale-110"
                            data-testid={`star-${i + 1}`}
                          >
                            <Star
                              className={`w-7 h-7 transition-colors ${i < reviewRating ? 'text-amber-400' : 'text-gray-200'}`}
                              fill="currentColor"
                              strokeWidth={0}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Comment (optional)</label>
                      <Textarea
                        value={reviewComment}
                        onChange={(e) => setReviewComment(e.target.value)}
                        placeholder="Tell us about your experience..."
                        className="rounded-lg"
                        rows={3}
                        data-testid="review-comment-input"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSubmitReview}
                        disabled={submittingReview}
                        className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg"
                        data-testid="submit-review-btn"
                      >
                        {submittingReview ? 'Submitting...' : 'Submit Review'}
                      </Button>
                      <Button variant="outline" onClick={() => setShowReview(false)} className="rounded-lg">Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Activity Log */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 animate-fade-in stagger-3" data-testid="activity-log">
              <h2 className="font-heading text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Activity Log</h2>
              {logs.length === 0 ? (
                <p className="text-sm text-gray-400">No activity recorded yet</p>
              ) : (
                <div className="space-y-3">
                  {logs.map(log => (
                    <div key={log.id} className="flex items-start gap-3 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#D4A843] mt-2 shrink-0" />
                      <div>
                        <p className="text-gray-700">
                          <span className="font-medium">{log.actor_name}</span> {log.action.replace('_', ' ')}
                          {log.details && Object.keys(log.details).length > 0 && (
                            <span className="text-gray-400"> — {JSON.stringify(log.details)}</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400">{new Date(log.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Chat */}
          <div className="lg:col-span-1 animate-fade-in stagger-2">
            <div className="sticky top-6">
              <ChatPanel ticketId={id} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
